import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, Home, Search, ChevronUp, ChevronDown } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { getTeamTransactions, duplicateTransaction } from '../services/transactionService';
import { getLogoBooleanTheme, getCurrentThemeIsDark } from '../utils/themeUtils';
import { goldButton } from '../styles/gold';

export default function Transactions() {
  const navigate = useNavigate();
  const { user, userRole } = useAuth();
  const [searchQuery, setSearchQuery] = useState('');
  const [sortField, setSortField] = useState('date');
  const [sortOrder, setSortOrder] = useState('desc');
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Dark theme for transactions (black background)
  const isDarkTheme = true; // Black header: use the white logo
  const logoSrc = getLogoBooleanTheme(isDarkTheme);

  // Load transactions from Firebase
  useEffect(() => {
    const loadTransactions = async () => {
      if (!user) { setLoading(false); return; }
      try {
        setLoading(true);
        setError(null);
        // Never spin forever: a denied or stalled read shows an error after 20 seconds.
        const data = await Promise.race([
          getTeamTransactions(user.uid, userRole),
          new Promise((_, reject) => setTimeout(() => reject(new Error('Timed out loading transactions')), 20000)),
        ]);
        setTransactions(data || []);
      } catch (err) {
        console.error('Error loading transactions:', err);
        setError(err?.code === 'PERMISSION_DENIED' || /permission/i.test(err?.message || '') ? 'You do not have permission to load these transactions' : (err?.message?.includes('Timed out') ? 'Transactions are taking too long to load. Please refresh.' : 'Failed to load transactions'));
      } finally {
        setLoading(false);
      }
    };

    loadTransactions();
  }, [user?.uid, userRole]);

  // Filter and sort transactions
  const filteredTransactions = transactions.filter(t => {
    const query = searchQuery.toLowerCase();
    return (
      t.firstName?.toLowerCase().includes(query) ||
      t.lastName?.toLowerCase().includes(query) ||
      t.email?.toLowerCase().includes(query) ||
      t.phone?.includes(query) ||
      t.propertyAddress?.toLowerCase().includes(query) ||
      t.type?.toLowerCase().includes(query)
    );
  });

  const STATUS_LABELS = {
    pending: 'Pending',
    incoming: 'Incoming',
    live: 'Live Listing',
    'under-contract': 'Under Contract',
    'listing-active': 'Active Listing',
    closed: 'Closed',
  };
  const STATUS_COLORS = {
    pending: '#000000',
    incoming: '#ffffff',
    live: '#D4AF37',
    'under-contract': '#000000',
    'listing-active': '#ffffff',
    closed: '#D4AF37',
  };
  const STATUS_TEXT = {
    incoming: '#000000',
    live: '#000000',
    'listing-active': '#000000',
    closed: '#000000',
  };
  // A buyer with no saved status is under contract (they have contract dates); sellers with none show New.
  const effectiveStatus = (t) => t.status || (t.type === 'Buyer' ? 'under-contract' : '');
  const statusLabel = (t) => STATUS_LABELS[effectiveStatus(t)] || (effectiveStatus(t) ? effectiveStatus(t) : 'New');
  const statusColor = (t) => STATUS_COLORS[effectiveStatus(t)] || '#7f7f7f';

  const sortedTransactions = [...filteredTransactions].sort((a, b) => {
    let aValue = a[sortField] || '';
    let bValue = b[sortField] || '';

    if (sortField === 'expiration') {
      aValue = new Date(a.expirationDate ? a.expirationDate + 'T00:00:00' : 0);
      bValue = new Date(b.expirationDate ? b.expirationDate + 'T00:00:00' : 0);
    } else if (sortField === 'status') {
      aValue = (a.status === 'pending' ? 'under-contract' : a.status) || '';
      bValue = (b.status === 'pending' ? 'under-contract' : b.status) || '';
    } else if (sortField === 'date') {
      aValue = new Date(a.closingDate ? a.closingDate + 'T00:00:00' : 0);
      bValue = new Date(b.closingDate ? b.closingDate + 'T00:00:00' : 0);
    } else if (sortField === 'name') {
      aValue = `${a.firstName} ${a.lastName}`;
      bValue = `${b.firstName} ${b.lastName}`;
    }

    if (typeof aValue === 'string') {
      aValue = aValue.toLowerCase();
      bValue = bValue.toLowerCase();
    }

    if (sortOrder === 'asc') {
      return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
    } else {
      return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
    }
  });

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('desc');
    }
  };

  const isMine = (transaction) => !transaction.ownerUid || transaction.ownerUid === user?.uid;

  const handleDuplicate = async (transaction) => {
    try {
      const newId = await duplicateTransaction(user.uid, transaction, transaction.id);
      navigate(`/transactions/${newId}`);
    } catch (err) {
      console.error('Error duplicating transaction:', err);
      setError('Could not duplicate this transaction');
    }
  };

  const handleDeleteOrLost = (transaction) => {
    navigate(`/transactions/${transaction.id}`, { state: { transaction, action: 'menu' } });
  };

  const handleViewTransaction = (transaction) => {
    // TODO: Navigate to transaction details page
    navigate(`/transactions/${transaction.id}`, { state: { transaction } });
  };

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <button onClick={() => navigate('/dashboard')} style={styles.logoButton}>
          <img src={logoSrc} alt="RESIDENCE | eXp Realty" style={styles.logo} />
        </button>
        <h1 style={styles.headerTitle}>Transactions</h1>
        <button
          onClick={() => navigate('/transactions/new')}
          style={styles.newTransactionButton}
        >
          <Plus size={20} />
          New Transaction
        </button>
      </div>

      {/* Main Content */}
      <div style={styles.mainContent}>
        {/* Error Message */}
        {error && (
          <div style={styles.errorBox}>
            <p style={styles.errorText}>{error}</p>
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div style={styles.emptyState}>
            <p style={styles.emptyText}>Loading transactions...</p>
          </div>
        )}

        {/* Search and Sort Bar */}
        {!loading && (
          <div className="tx-controls" style={styles.controlsBar}>
            <div style={styles.searchContainer}>
              <Search size={18} style={{color: '#999'}} />
              <input
                type="text"
                placeholder="Search by name, email, phone, property, or type..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={styles.searchInput}
              />
            </div>
            <div style={styles.sortGroup}>
              <label style={styles.sortLabel} htmlFor="sortBy">Sort by</label>
              <select
                id="sortBy"
                value={sortField}
                onChange={(e) => { setSortField(e.target.value); setSortOrder('asc'); }}
                style={styles.sortSelect}
              >
                <option value="status">Status</option>
                <option value="expiration">Expiration Date</option>
                <option value="date">Anticipated Closing</option>
                <option value="type">Type</option>
                <option value="name">Client Name</option>
              </select>
              <button
                type="button"
                onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                style={styles.sortDirButton}
                title="Reverse sort order"
              >
                {sortOrder === 'asc' ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
              </button>
            </div>
          </div>
        )}

        {/* Transactions List */}
        {!loading && sortedTransactions.length > 0 ? (
          <div style={styles.tableContainer}>
            <table style={styles.table}>
              <thead>
                <tr style={styles.tableHeader}>
                  <th style={{...styles.tableCell, ...styles.headerCell, cursor: 'pointer'}} onClick={() => handleSort('name')}>
                    <div style={styles.headerContent}>
                      Client Name
                      {sortField === 'name' && (sortOrder === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />)}
                    </div>
                  </th>
                  <th style={{...styles.tableCell, ...styles.headerCell}}>Property Address</th>
                  <th style={{...styles.tableCell, ...styles.headerCell, cursor: 'pointer'}} onClick={() => handleSort('type')}>
                    <div style={styles.headerContent}>
                      Type
                      <ChevronDown
                        size={14}
                        style={{
                          opacity: sortField === 'type' ? 1 : 0.45,
                          transform: sortField === 'type' && sortOrder === 'asc' ? 'rotate(180deg)' : 'none',
                          transition: 'transform 0.15s ease',
                        }}
                      />
                    </div>
                  </th>
                  <th style={{...styles.tableCell, ...styles.headerCell, cursor: 'pointer'}} onClick={() => handleSort('status')}>
                    <div style={styles.headerContent}>
                      Status
                      {sortField === 'status' && (sortOrder === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />)}
                    </div>
                  </th>
                  <th style={{...styles.tableCell, ...styles.headerCell, cursor: 'pointer'}} onClick={() => handleSort('date')}>
                    <div style={styles.headerContent}>
                      Anticipated Closing
                      {sortField === 'date' && (sortOrder === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />)}
                    </div>
                  </th>
                  <th style={{...styles.tableCell, ...styles.headerCell}}>Expiration Date</th>
                  <th style={{...styles.tableCell, ...styles.headerCell}}>Action</th>
                </tr>
              </thead>
              <tbody>
                {sortedTransactions.map((transaction, index) => (
                  <tr key={transaction.id || index} style={styles.tableRow}>
                    <td style={styles.tableCell}>
                      <span style={styles.clientName}>{(transaction.firstName || transaction.lastName) ? `${transaction.firstName || ''} ${transaction.lastName || ''}`.trim() : (transaction.clientName || '')}</span>
                    </td>
                    <td style={styles.tableCell}>
                      <span style={styles.address}>{transaction.propertyAddress || transaction.desiredLocation || 'N/A'}</span>
                    </td>
                    <td style={styles.tableCell}>
                      <span style={{
                        display: 'inline-block',
                        fontSize: '12px',
                        fontWeight: 600,
                        padding: '4px 10px',
                        borderRadius: '4px',
                        whiteSpace: 'nowrap',
                        ...(String(transaction.type || '').toLowerCase() === 'buyer'
                          ? { color: '#5c4413', backgroundColor: '#ffffff', border: '1px solid #D4AF37' }
                          : { color: '#ffffff', backgroundColor: '#6b6b6b', border: '1px solid #6b6b6b' }),
                      }}>{transaction.type}</span>
                    </td>
                    <td style={styles.tableCell}>
                      <span style={{
                        display: 'inline-block',
                        fontSize: '12px',
                        fontWeight: 600,
                        color: STATUS_TEXT[transaction.status] || '#ffffff',
                        backgroundColor: statusColor(transaction),
                        padding: '4px 10px',
                        borderRadius: '4px',
                        whiteSpace: 'nowrap',
                      }}>
                        {statusLabel(transaction)}
                      </span>
                    </td>
                    <td style={styles.tableCell}>
                      <span style={styles.date}>
                        {transaction.closingDate ? new Date(transaction.closingDate + 'T00:00:00').toLocaleDateString() : '—'}
                      </span>
                    </td>
                    <td style={styles.tableCell}>
                      <span style={styles.date}>
                        {transaction.expirationDate ? new Date(transaction.expirationDate + 'T00:00:00').toLocaleDateString() : '—'}
                      </span>
                    </td>
                    <td style={styles.tableCell}>
                      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                        <button
                          onClick={() => handleViewTransaction(transaction)}
                          style={styles.viewButton}
                        >
                          View Details
                        </button>
                        {String(transaction.type || '').toLowerCase() === 'seller' && !['closed', 'lost', 'draft', 'pending', 'under-contract'].includes(transaction.status) && !transaction.isDraft && (
                          <button
                            onClick={() => navigate(`/transactions/${transaction.id}/under-contract`)}
                            style={{ ...styles.viewButton, backgroundColor: '#1a1a1a', color: '#ffffff' }}
                          >
                            Under Contract Form
                          </button>
                        )}
                        {isMine(transaction) && (
                          <>
                            <button
                              onClick={() => handleDuplicate(transaction)}
                              style={styles.viewButton}
                              title="Duplicate transaction (dates are cleared)"
                            >
                              Duplicate
                            </button>
                            <button
                              onClick={() => handleDeleteOrLost(transaction)}
                              style={{ ...styles.viewButton, color: '#9b2c2c', border: '1px solid #9b2c2c' }}
                              title="Delete or mark as lost"
                            >
                              Delete / Lost
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : loading ? null : (
          <div style={styles.emptyState}>
            <p style={styles.emptyText}>No transactions yet.</p>
            <p style={styles.emptySubtext}>Click "New Transaction" to create your first transaction.</p>
          </div>
        )}
      </div>
    </div>
  );
}

const styles = {
  container: {
    minHeight: '100vh',
    backgroundColor: '#000000',
    color: '#1a1a1a',
    fontFamily: 'Roboto, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif'
  },
  header: {
    backgroundColor: '#1a1a1a',
    borderBottom: '2px solid #D4AF37',
    padding: '20px 40px',
    display: 'flex',
    alignItems: 'center',
    gap: '20px',
    justifyContent: 'space-between'
  },
  logoButton: {
    backgroundColor: 'transparent',
    border: 'none',
    cursor: 'pointer',
    padding: '0',
    display: 'flex',
    alignItems: 'center'
  },
  logo: {
    height: '40px',
    width: 'auto',
    maxWidth: '150px'
  },
  headerTitle: {
    fontSize: '24px',
    fontWeight: 'bold',
    color: '#ffffff',
    margin: '0',
    letterSpacing: '2px',
    flex: 1,
    textAlign: 'center',
    fontFamily: 'Roboto, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif'
  },
  newTransactionButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    ...goldButton,
    padding: '10px 20px',
    borderRadius: '4px',
    fontWeight: '600',
    cursor: 'pointer',
    fontSize: '14px',
    transition: 'all 0.3s ease',
    textTransform: 'uppercase',
    letterSpacing: '0.5px'
  },
  mainContent: {
    padding: '40px',
    margin: '16px',
    backgroundColor: '#ffffff',
    borderRadius: '10px',
    minHeight: 'calc(100vh - 170px)'
  },
  controlsBar: {
    marginBottom: '32px',
    display: 'flex',
    gap: '16px',
    alignItems: 'center'
  },
  sortGroup: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  sortLabel: {
    fontSize: '13px',
    color: '#1a1a1a',
    fontWeight: 600,
  },
  sortSelect: {
    padding: '10px 12px',
    border: '1px solid #d8c9a8',
    borderRadius: '4px',
    fontSize: '14px',
    backgroundColor: '#ffffff',
    color: '#1a1a1a',
  },
  sortDirButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '8px',
    border: '1px solid #d8c9a8',
    borderRadius: '4px',
    background: '#ffffff',
    cursor: 'pointer',
    color: '#1a1a1a',
  },
  searchContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    backgroundColor: '#ffffff',
    border: '1px solid #d8c9a8',
    borderRadius: '4px',
    padding: '12px 16px',
    flex: 1,
    maxWidth: '400px'
  },
  searchInput: {
    backgroundColor: 'transparent',
    border: 'none',
    color: '#1a1a1a',
    fontSize: '14px',
    outline: 'none',
    flex: 1,
    fontFamily: 'Roboto, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif'
  },
  tableContainer: {
    backgroundColor: '#ffffff',
    border: '1px solid #d8c9a8',
    borderRadius: '8px',
    overflow: 'hidden'
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontFamily: 'Roboto, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif'
  },
  tableHeader: {
    backgroundColor: '#ffffff',
    borderBottom: '2px solid #D4AF37'
  },
  headerCell: {
    backgroundColor: '#ffffff',
    color: '#8C6D1F',
    fontWeight: '600',
    textAlign: 'left',
    padding: '16px'
  },
  headerContent: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  },
  tableCell: {
    padding: '16px',
    borderBottom: '1px solid #333333',
    fontSize: '14px'
  },
  tableRow: {
    transition: 'background-color 0.2s ease',
    cursor: 'pointer'
  },
  clientName: {
    fontWeight: '600',
    color: '#1a1a1a'
  },
  address: {
    color: '#444444',
    fontSize: '13px'
  },
  badge: {
    backgroundColor: '#D4AF37',
    color: '#000000',
    padding: '4px 12px',
    borderRadius: '4px',
    fontSize: '12px',
    fontWeight: '600'
  },
  date: {
    color: '#7f7f7f',
    fontSize: '13px'
  },
  contact: {
    color: '#7f7f7f',
    fontSize: '13px'
  },
  viewButton: {
    ...goldButton,
    padding: '8px 16px',
    borderRadius: '4px',
    fontWeight: '600',
    fontSize: '12px',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    fontFamily: 'Roboto, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif'
  },
  emptyState: {
    textAlign: 'center',
    padding: '60px 40px'
  },
  emptyText: {
    fontSize: '18px',
    fontWeight: '600',
    color: '#1a1a1a',
    margin: '0 0 8px 0',
    fontFamily: 'Roboto, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif'
  },
  emptySubtext: {
    fontSize: '14px',
    color: '#7f7f7f',
    margin: '0',
    fontFamily: 'Roboto, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif'
  },
  errorBox: {
    backgroundColor: '#ffffff',
    border: '1px solid #D4AF37',
    borderRadius: '4px',
    padding: '16px',
    marginBottom: '20px'
  },
  errorText: {
    color: '#1a1a1a',
    fontSize: '14px',
    margin: '0',
    fontFamily: 'Roboto, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif'
  }
};

// Shared with the Pipeline list so both lists look the same.
export { styles as transactionListStyles };
